import { useCallback, useEffect, useState } from "react";
import { Copy, Share2, Link2, Ban } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Field } from "@/components/AuthShell";
import { ErrorBox, Spinner } from "@/components/Bits";
import ConfirmButton from "@/components/ConfirmButton";

const STATE = { active: ["Active", "text-[#B7F52A]"], expired: ["Expirée", "text-slate-500"], revoked: ["Révoquée", "text-red-300"], exhausted: ["Épuisée", "text-slate-500"] };

function NewLink({ invite }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(invite.link); toast.success("Lien copié"); } catch (e) { toast.error("Copie impossible : sélectionnez le lien manuellement"); }
  };
  const share = () => navigator.share({ title: "Ma Petite Ligue", text: "Rejoins ma ligue sur Ma Petite Ligue !", url: invite.link }).catch(() => {});
  return (
    <div className="mpl-card border-[#B7F52A]/40 p-4" data-testid="new-invite-link-box">
      <p className="mb-2 text-sm font-bold text-[#B7F52A]">Lien créé</p>
      <p className="mb-3 text-xs text-slate-400">Pour votre sécurité, ce lien n'est affiché qu'une seule fois. Copiez-le maintenant.</p>
      <input readOnly value={invite.link} className="mpl-input font-mono-num text-xs" data-testid="new-invite-link" onFocus={(e) => e.target.select()} />
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={copy} className="mpl-btn-primary" data-testid="copy-invite-link"><Copy size={15} /> Copier</button>
        {typeof navigator.share === "function" && <button onClick={share} className="mpl-btn-ghost" data-testid="share-invite-link"><Share2 size={15} /> Partager</button>}
      </div>
      {invite.email && (
        <p className="mt-3 text-xs text-slate-400" data-testid="invite-email-status">
          {invite.email_sent ? `Courriel envoyé à ${invite.email}.` : `Le courriel n'a pas pu être envoyé à ${invite.email} : partagez le lien vous-même.`}
          {invite.in_app_sent && " Une notification a aussi été ajoutée dans son application."}
        </p>
      )}
    </div>
  );
}

export default function InvitationsTab({ league }) {
  const { tz } = useAuth();
  const [list, setList] = useState(null);
  const [form, setForm] = useState({ expires_in_hours: 168, max_uses: 5, email: "" });
  const [created, setCreated] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/leagues/${league.id}/invitations`).then(({ data }) => setList(data)).catch((e) => setError(errMsg(e))); }, [league.id]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/leagues/${league.id}/invitations`, { ...form, email: form.email || null });
      setCreated(data);
      load();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  const revoke = async (inv) => {
    try { await api.delete(`/leagues/${league.id}/invitations/${inv.id}`); toast.success("Invitation révoquée"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const full = Boolean(league.max_members) && league.member_count >= league.max_members;
  return (
    <div className="space-y-6" data-testid="invitations-tab">
      {league.archived ? <p className="mpl-card p-4 text-sm text-slate-400" data-testid="invite-archived">Ligue archivée : aucune nouvelle invitation possible.</p> : <form onSubmit={create} className="mpl-card space-y-4 p-4" data-testid="invite-form">
        <h2 className="flex items-center gap-2 text-base font-bold"><Link2 size={17} className="text-[#B7F52A]" /> Inviter des amis</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Expiration">
            <select className="mpl-input" value={form.expires_in_hours} onChange={(e) => setForm({ ...form, expires_in_hours: Number(e.target.value) })} data-testid="invite-expiry-select">
              <option value={24}>24 heures</option><option value={72}>3 jours</option><option value={168}>7 jours</option><option value={720}>30 jours</option>
            </select>
          </Field>
          <Field label="Nombre d'utilisations">
            <input className="mpl-input" type="number" min={1} max={199} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: Number(e.target.value) })} data-testid="invite-max-uses-input" />
          </Field>
        </div>
        <Field label="Courriel de l'invité (facultatif)" hint="Un courriel d'invitation lui sera envoyé.">
          <input className="mpl-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" />
        </Field>
        {full && <p className="text-sm text-amber-300" data-testid="league-full-note">La ligue est complète ({league.max_members} places). Augmente le nombre de places dans Paramètres.</p>}
        {error && <ErrorBox message={error} />}
        <button className="mpl-btn-primary" disabled={busy || full} data-testid="invite-create-button">{busy ? "Création…" : "Générer un lien"}</button>
      </form>}
      {created && <NewLink invite={created} />}
      <section>
        <h2 className="mb-3 text-sm font-bold">Invitations</h2>
        {!list ? <Spinner /> : (
          <div className="mpl-card divide-y divide-white/[0.05]" data-testid="invitations-list">
            {list.length === 0 && <p className="p-4 text-sm text-slate-400">Aucune invitation pour l'instant.</p>}
            {list.map((i) => (
              <div key={i.id} data-testid={`invitation-row-${i.id}`} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                <div className="min-w-0">
                  <p className={`font-semibold ${STATE[i.state][1]}`} data-testid={`invitation-state-${i.id}`}>{STATE[i.state][0]}</p>
                  <p className="truncate text-xs text-slate-400">{i.uses}/{i.max_uses} utilisations · expire le {fmtDateTime(i.expires_at, tz)}{i.email ? ` · ${i.email}` : ""}</p>
                </div>
                {i.state === "active" && (
                  <ConfirmButton label={<><Ban size={14} /> Révoquer</>} className="mpl-btn-ghost px-3 py-1.5 text-xs" testId={`revoke-invitation-${i.id}`}
                    title="Révoquer cette invitation ?" description="Le lien ne fonctionnera plus. Cette action est définitive." confirmLabel="Révoquer" onConfirm={() => revoke(i)} />
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
